import React from "react";
import Select from "react-select";
import { AlertTriangle, CheckCircle2, Search, UploadCloud, X } from "lucide-react";
import TranslateText from "../../TranslateText";

const AddStrikeModal = ({
  t,
  formRef,
  fileInputRef,
  profileImageSrc,
  displayName,
  getInitials,
  user,
  normalizedRole,
  activeStrikes,
  strikeForm,
  errors,
  categorySelectedOption,
  availableCategories,
  handleCategoryChange,
  CategoryOption,
  selectStyles,
  selectedCategoryConfig,
  severityOptions,
  updateField,
  optionalReferenceTypes,
  optionalReferenceOption,
  optionalReferenceType,
  setOptionalReferenceType,
  setReferenceSearch,
  setStrikeForm,
  setErrors,
  ReferenceTypeOption,
  ReferenceTypeSingleValue,
  selectedReferenceType,
  referenceOptions,
  selectedReference,
  referenceSearch,
  referenceLoading,
  ReferenceOption,
  ReferenceSingleValue,
  getReferencePlaceholder,
  dragActive,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleEvidenceChange,
  getFileIcon,
  getFileExtension,
  formatBytes,
  removeEvidence,
  maxEvidenceFiles,
  isStrikeFormIncomplete,
  submitLoading,
  onClose,
  onSubmit,
}) => (
  <div className="strike-modal-backdrop strike-form-modal-backdrop" role="presentation">
    <form className="strike-panel strike-form-modal" ref={formRef} onSubmit={onSubmit} role="dialog" aria-modal="true" aria-labelledby="strike-form-title">
      <div className="strike-panel-header">
        <div>
          <h3 className="strike-panel-title" id="strike-form-title">{t("applyStrike.title")}</h3>
          <p className="strike-panel-subtitle">{t("applyStrike.subtitle")}</p>
        </div>
        <button type="button" className="strike-close-btn" onClick={onClose} aria-label={t("applyStrike.closeForm")}>
          <X size={18} />
        </button>
      </div>

      <div className="modalbody-scroll-bar">
        <div className="strike-user-summary">
          <div className="strike-user-avatar">
            {profileImageSrc ? <img src={profileImageSrc} alt={displayName} /> : <span>{getInitials(displayName)}</span>}
          </div>
          <div className="strike-user-main">
            <h4>{displayName}</h4>
            <p>{user?.email || t("applyStrike.noEmail")}</p>
          </div>
          <div className="strike-user-meta">
            <div>
              <span>{t("applyStrike.role")}</span>
              <strong>{normalizedRole || t("applyStrike.unknown")}</strong>
            </div>
            <div>
              <span>{t("applyStrike.activeStrikesLabel")}</span>
              <strong>{activeStrikes}</strong>
            </div>
          </div>
        </div>

        <section className="strike-section">
          <div className="strike-section-header">
            <div>
              <h4>{t("applyStrike.violationDetails")}</h4>
              <p>{t("applyStrike.violationHelper")}</p>
            </div>
          </div>

          <div className="strike-form-grid">
            <div className="strike-field" data-field="category">
              <label className="strike-field-label" htmlFor="strike-category">
                {t("applyStrike.category")} <span className="strike-required">*</span>
              </label>
              <Select
                inputId="strike-category"
                className="strike-category-select"
                classNamePrefix="strike-select"
                options={availableCategories}
                value={categorySelectedOption}
                onChange={(option) => handleCategoryChange(option?.value || "")}
                components={{ Option: CategoryOption }}
                styles={selectStyles}
                placeholder={availableCategories.length ? t("applyStrike.placeholders.selectCategory") : t("applyStrike.noCategories")}
                isDisabled={!availableCategories.length}
                aria-invalid={!!errors.category}
                aria-describedby={errors.category ? "strike-category-error" : undefined}
              />
              {errors.category ? <span id="strike-category-error" className="strike-error">{errors.category}</span> : null}
            </div>

            <div className={`strike-field ${!strikeForm.category ? "strike-field-disabled" : ""}`} data-field="severity">
              <label className="strike-field-label">
                {t("applyStrike.severityLabel")} <span className="strike-required">*</span>
              </label>
              <div className="strike-severity-grid">
                {severityOptions.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    disabled={!strikeForm.category}
                    className={`strike-severity-card strike-severity-${item.value.toLowerCase()} ${strikeForm.severity === item.value ? "active" : ""}`}
                    onClick={() => updateField("severity", item.value)}
                    aria-pressed={strikeForm.severity === item.value}
                  >
                    <span className="strike-severity-dot" />
                    <strong>{item.label}</strong>
                    <small>{item.description}</small>
                  </button>
                ))}
              </div>
              {!strikeForm.category ? <span className="strike-helper">{t("applyStrike.selectCategoryToContinue")}</span> : null}
              {errors.severity ? <span className="strike-error">{errors.severity}</span> : null}
            </div>

            <div className={`strike-field strike-field-full ${!strikeForm.category ? "strike-field-disabled" : ""}`} data-field="reference_id">
              <label className="strike-field-label" htmlFor="strike-reference">
                {t("applyStrike.relatedReference")} {selectedCategoryConfig?.referenceRequired ? <span className="strike-required">*</span> : null}
              </label>
              {selectedCategoryConfig?.referenceType === "OPTIONAL" ? (
                <Select
                  inputId="strike-reference-type"
                  className="strike-reftype-select"
                  classNamePrefix="strike-select"
                  options={optionalReferenceTypes}
                  value={optionalReferenceOption}
                  onChange={(option) => {
                    const nextType = option?.value || "";
                    setOptionalReferenceType(nextType);
                    setReferenceSearch("");
                    setStrikeForm((prev) => ({ ...prev, reference_type: nextType, reference_id: "", reference_data: null }));
                    setErrors((prev) => ({ ...prev, reference_id: "" }));
                  }}
                  components={{ Option: ReferenceTypeOption, SingleValue: ReferenceTypeSingleValue }}
                  styles={selectStyles}
                  isSearchable={false}
                  isDisabled={!strikeForm.category}
                />
              ) : null}

              {strikeForm.category && selectedReferenceType ? (
                <div className="strike-reference-wrapper">
                  <Select
                    inputId="strike-reference"
                    classNamePrefix="strike-select"
                    options={referenceOptions}
                    value={selectedReference}
                    inputValue={referenceSearch}
                    onInputChange={(value, meta) => {
                      if (meta.action === "input-change") setReferenceSearch(value);
                    }}
                    onChange={(option) => {
                      setStrikeForm((prev) => ({
                        ...prev,
                        reference_id: option?.value || "",
                        reference_type: option?.referenceType || selectedReferenceType || "",
                        reference_data: option || null,
                      }));
                      setErrors((prev) => ({ ...prev, reference_id: "" }));
                    }}
                    components={{ Option: ReferenceOption, SingleValue: ReferenceSingleValue }}
                    styles={selectStyles}
                    placeholder={getReferencePlaceholder(selectedReferenceType, t)}
                    isLoading={referenceLoading}
                    isClearable
                    noOptionsMessage={() => referenceLoading ? t("applyStrike.loadingReferences") : t("applyStrike.noMatchingReferences")}
                    aria-invalid={!!errors.reference_id}
                    aria-describedby={errors.reference_id ? "strike-reference-error" : undefined}
                  />
                </div>
              ) : (
                <div className="strike-empty-reference">
                  <Search size={16} />
                  <span>{strikeForm.category ? t("applyStrike.noRelatedRecordSelected") : t("applyStrike.selectCategoryToContinue")}</span>
                </div>
              )}
              {selectedReference ? (
                <div className="strike-selected-reference">
                  <div>
                    <span>{selectedReference.status || selectedReference.referenceType}</span>
                    <strong>{selectedReference.label}</strong>
                    <small>{selectedReference.code}</small>
                  </div>
                  <TranslateText text={selectedReference.meta} fallback="-" as="p" />
                </div>
              ) : null}
              {referenceLoading ? <span className="strike-helper">{t("applyStrike.loadingReferenceOptions")}</span> : null}
              {errors.reference_id ? <span id="strike-reference-error" className="strike-error">{errors.reference_id}</span> : null}
            </div>

            <div className={`strike-field strike-field-full ${!strikeForm.category ? "strike-field-disabled" : ""}`} data-field="reason">
              <label className="strike-field-label" htmlFor="strike-reason">
                {t("applyStrike.reason")} <span className="strike-required">*</span>
              </label>
              <input
                id="strike-reason"
                type="text"
                className="strike-input"
                value={strikeForm.reason}
                maxLength={255}
                disabled={!strikeForm.category}
                placeholder={t("applyStrike.placeholders.reason")}
                onChange={(event) => updateField("reason", event.target.value)}
                aria-required="true"
                aria-invalid={!!errors.reason}
                aria-describedby={errors.reason ? "strike-reason-error" : "strike-reason-count"}
              />
              <div className="strike-field-footer">
                {errors.reason ? <span id="strike-reason-error" className="strike-error">{errors.reason}</span> : <span />}
                <span id="strike-reason-count" className="strike-counter">{strikeForm.reason.length} / 255</span>
              </div>
            </div>

            <div className={`strike-field strike-field-full ${!strikeForm.category ? "strike-field-disabled" : ""}`} data-field="description">
              <label className="strike-field-label" htmlFor="strike-description">
                {t("applyStrike.description")} <span className="strike-required">*</span>
              </label>
              <textarea
                id="strike-description"
                className="strike-textarea"
                rows="5"
                value={strikeForm.description}
                maxLength={2000}
                disabled={!strikeForm.category}
                placeholder={t("applyStrike.placeholders.description")}
                onChange={(event) => updateField("description", event.target.value)}
                aria-required="true"
                aria-invalid={!!errors.description}
                aria-describedby={errors.description ? "strike-description-error" : "strike-description-count"}
              />
              <div className="strike-field-footer">
                {errors.description ? <span id="strike-description-error" className="strike-error">{errors.description}</span> : <span />}
                <span id="strike-description-count" className="strike-counter">{strikeForm.description.length} / 2000</span>
              </div>
            </div>
          </div>
        </section>

        <section className={`strike-section ${!strikeForm.category ? "strike-section-disabled" : ""}`} data-field="evidence">
          <div className="strike-evidence-header">
            <div>
              <h4>{t("applyStrike.evidence")}  {selectedCategoryConfig?.evidenceRequired && <span className="strike-required">*</span>}</h4>
              <p>{t("applyStrike.evidenceTypes")}</p>
            </div>
            {/* <span className={`strike-evidence-badge ${selectedCategoryConfig?.evidenceRequired ? "required" : ""}`}>
              {selectedCategoryConfig?.evidenceRequired ? t("applyStrike.required") : t("applyStrike.optional")}
            </span> */}
          </div>

          <div
            className={`strike-upload-zone ${dragActive ? "strike-upload-zone-active" : ""} ${!strikeForm.category ? "strike-upload-zone-disabled" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              disabled={!strikeForm.category}
              accept="image/*"
              onChange={handleEvidenceChange}
            />
            <div className="strike-upload-icon">
              <UploadCloud size={28} />
            </div>
            <div className="strike-upload-copy">
              <strong>{strikeForm.category ? t("applyStrike.uploadEvidence") : t("applyStrike.uploadDisabledTitle")}</strong>
              <span>{strikeForm.category ? t("applyStrike.uploadHelper") : t("applyStrike.uploadDisabledHelper")}</span>
              <small>{t("applyStrike.fileLimit")}</small>
            </div>
            <div className="strike-upload-actions">
              <button type="button" className="strike-browse-btn" disabled={!strikeForm.category} onClick={() => fileInputRef.current?.click()}>
                {t("applyStrike.browseFiles")}
              </button>
            </div>
          </div>
          {errors.evidence ? <span className="strike-error">{errors.evidence}</span> : null}

          <div className="strike-evidence-list-header">
            <strong>{t("applyStrike.selectedEvidence")}</strong>
            <span>{t("applyStrike.fileCount", { count: strikeForm.evidence.length, max: maxEvidenceFiles })}</span>
          </div>

          {strikeForm.evidence.length ? (
            <div className="strike-evidence-grid">
              {strikeForm.evidence.map((item) => (
                <div className="strike-evidence-card" key={item.id}>
                  <div className="strike-evidence-preview">
                    {item.file.type.startsWith("image/") && item.previewUrl ? (
                      <img src={item.previewUrl} alt={t("applyStrike.previewAlt", { file: item.file.name })} />
                    ) : (
                      <div className="strike-evidence-file-icon">{getFileIcon(item.file)}</div>
                    )}
                  </div>
                  <div className="strike-evidence-info">
                    <strong title={item.file.name}>{item.file.name}</strong>
                    <span>{getFileExtension(item.file.name).toUpperCase() || t("applyStrike.fileFallback")} - {formatBytes(item.file.size)}</span>
                  </div>
                  <button type="button" className="strike-evidence-remove" onClick={() => removeEvidence(item.id)} aria-label={t("applyStrike.removeFileAria", { file: item.file.name })}>
                    {t("applyStrike.remove")}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="strike-empty-evidence">
              <AlertTriangle size={18} />
              <span>{t("applyStrike.noEvidence")}</span>
            </div>
          )}
        </section>
      </div>

      <div className="strike-form-actions">
        <div className="strike-submit-helper">
          {isStrikeFormIncomplete ? t("applyStrike.completeRequired") : t("applyStrike.ready")}
        </div>
        <div className="strike-action-buttons">
          <button type="button" className="strike-btn-cancel" onClick={onClose}>
            {t("applyStrike.cancel")}
          </button>
          <button type="submit" className="strike-btn-submit" disabled={submitLoading}>
            {submitLoading ? t("applyStrike.applying") : t("applyStrike.title")}
            {!submitLoading ? <CheckCircle2 size={17} /> : null}
          </button>
        </div>
      </div>
    </form>
  </div>
);

export default AddStrikeModal;
